import { Code2, GraduationCap, Scan } from 'lucide-react';

export const profile = {
  name: '酒井 真彦',
  englishName: 'MASAHIKO SAKAI',
  portrait: {
    src: '/images/masahiko-sakai.jpg',
    srcSet: '/images/masahiko-sakai-480.webp 480w, /images/masahiko-sakai-960.webp 960w',
    width: 960,
    height: 1203,
    alt: '酒井真彦の顔写真',
  },
  institution: '公立大学法人会津大学大学院',
  school: 'コンピュータ理工学研究科',
  major: 'コンピュータ・情報システム学専攻',
  degree: '博士前期課程',
  graduation: '2028年3月 修了予定',
  headline: '現場の声から、使えるものをつくる',
  value: 'リアリティを追求すること。自分の足で現場に赴き、一次情報を集め、実際に使えるものをつくることを大切にしています。',
  ambition: 'まずはエンジニアとして経験を積み、将来はPMへ。クライアントの近くで話を聞き、チームとともにプロジェクトを推進していきたいと考えています。',
  interests: ['ソフトウェア開発', 'AI', 'フィジカルAI'],
  licenses: ['高等学校教員免許（数学）', '高等学校教員免許（情報）', '中学校教員免許（数学）'],
  email: 'sakaimasahiko23@gmail.com',
  github: 'https://github.com/Sakaaaaai',
  udemy: 'https://www.udemy.com/user/jiu-jing-zhen-yan-3/',
};

export const udemyActivity = {
  title: ['伝えることが、', '次の学びになる'],
  paragraphs: [
    '教職で学んだ知識を生かし、わかりやすく伝えることを大切に、Udemyで講座を制作しています。',
    '人に伝えるために知識を整理することが、自分自身の理解を深める学習にもつながっています。今では、学びを講座としてアウトプットすることが、継続的な学習のよい習慣になっています。',
  ],
  metrics: [
    { value: '46', unit: '講座', label: 'これまでに制作した講座' },
    { value: '291', unit: '人', label: '総学習者' },
  ],
  asOf: '2026-09-13',
  asOfLabel: '2026年9月13日時点',
};

export const disciplines = [
  {
    number: '01', title: 'Engineering', subtitle: '課題を聞き、実装につなぐ',
    description: '大学1年から実務での開発を経験。Webサイトのフロントエンドから社内システム、AIを使った業務改善まで、課題に向き合いながら技術を広げています。',
    icon: Code2, tags: ['Web development', 'Team development', 'Dify'],
  },
  {
    number: '02', title: 'Education', subtitle: '「わからない」の根本を探る',
    description: '生徒の「わからない」を言語化・抽象化し、つまずきの根本を捉えることを重視。AIを教材づくりや模擬授業に活用し、数学を視覚的に理解できる教材も自作しました。',
    icon: GraduationCap, tags: ['数学・情報', '教材制作', 'AI活用'],
  },
  {
    number: '03', title: 'Research', subtitle: '自分の足で、確かめに行く',
    description: '電気柵見回りロボットの撮影方法を改善する卒業研究に取り組みました。何度も山に足を運んで実証実験とデータ収集を重ね、情報処理学会でも発表しました。',
    icon: Scan, tags: ['AI', 'Robotics', 'Field experiments'],
  },
];

export const research = {
  "title": "電気柵見回りロボットの撮影効率を改善する",
  "label": "卒業研究 / AI・ロボティクス",
  "overview": "野生動物の侵入を防ぐ電気柵の巡回を支援するロボットを対象に、無駄な撮影と画像のブレを減らす撮影方法を提案・実装しました。",
  "contribution": "既存のRTK-GNSSによる自律走行ロボットを基盤に、撮影地点の計算、停止してからの撮影、撮影画像を処理するフローを改良。撮影方法と処理の効率化を提案し、実機に組み込んで評価しました。",
  "challenge": "模擬環境でうまく動いていたアルゴリズムが、実環境では想定どおりに動かないことが最も苦労した点です。何度も山に足を運び、実験とデータ収集を繰り返しながらパラメータを調整し、精度を高めました。現場の条件で使えるかを、実機による評価まで確かめました。",
  "methods": [
    {
      "title": "必要な撮影地点を計算",
      "text": "支柱間の距離、カメラの画角、画像の重複率から撮影地点と撮影枚数を算出。電気柵をカバーしながら、冗長な撮影を削減します。"
    },
    {
      "title": "停止撮影と画像処理を組み合わせる",
      "text": "各撮影地点で一時停止してブレを抑制。撮影枚数を考慮した画像キューを設け、撮影した画像を異常検出に利用できるよう処理フローを変更しました。"
    },
    {
      "title": "実フィールドで比較評価",
      "text": "福島県会津美里町の電気柵で、従来手法と提案手法を比較。撮影枚数、保存容量、消費電力・エネルギー、走行時間、検出精度を評価しました。"
    }
  ],
  "metrics": [
    {
      "value": "93.6%",
      "label": "撮影枚数を削減",
      "comparison": "2,981枚 → 191枚"
    },
    {
      "value": "93.4%",
      "label": "保存容量を削減",
      "comparison": "474.0 MB → 31.5 MB"
    },
    {
      "value": "25 → 100%",
      "label": "雑草接触の再現率",
      "comparison": "本実験での比較 / +75ポイント"
    }
  ],
  "conditions": "実験条件：総走行距離288 m、経由点97か所。従来手法・提案手法を各2回走行した平均値。同一の経路・走行距離・物体検出モデルで比較しました。",
  "tradeoff": "停止撮影により走行時間は596秒から622秒へ4.4%増加。一方、平均消費電力は6.9%、消費エネルギーは2.9%減少しました。雑草接触の再現率100%は、この実験条件における結果です。",
  "technologies": [
    "Python",
    "ROS 2 Jazzy",
    "Raspberry Pi 5",
    "RTK-GNSS",
    "SCOUT MINI",
    "YOLO11",
    "Depth Anything V2"
  ],
  "modelNote": "物体検出には既存の学習済みモデルを使用。深度推定にはDepth Anything V2を使用しました。",
  "fieldImage": "/images/research-field-route.png",
  "methodImage": "/images/research-capture-method.png",
  "video": {
    "src": "/media/fence-patrol-robot-v2.mp4",
    "webm": "/media/fence-patrol-robot-v2.webm",
    "poster": "/images/fence-patrol-robot-poster.jpg",
    "caption": "実機の動作動画 / 電気柵に沿って移動し、撮影地点で一時停止。",
    "description": "電気柵の横を巡回するロボットの動作を記録しました。撮影地点で停止し、撮影した画像を異常検出に利用する一連の処理を、実際のフィールドで検証しています。"
  },
  "publications": [
    {
      "type": "情報処理学会発表",
      "pdf": "/papers/ipsj-88-masahiko-sakai.pdf",
      "pages": 2,
      "location": "愛媛",
      "title": "電気柵の長さを考慮した撮影方法による電気柵見回りロボットの異常検出効率の改善",
      "authors": "酒井 真彦・仙波 翔吾・小平 行秀・冨岡 洋一・齋藤 寛",
      "note": "同研究について情報処理学会 第88回全国大会で発表。"
    },
    {
      "type": "卒業論文",
      "pdf": "/papers/graduation-thesis-masahiko-sakai.pdf",
      "pages": 7,
      "title": "Efficient Image Capturing Method for Electric Fence Patrol Robot Considering Fence Length",
      "authors": "Masahiko Sakai / 指導：Hiroshi Saito",
      "note": "撮影方法の提案、実装、実地実験による評価をまとめた学部研究。"
    }
  ]
};

export const journey = [
  {
    id: 'eyes-japan', date: '2022.06 — 2023.02', title: 'Eyes,JAPAN',
    role: 'フロントエンド開発 / 社内システム開発', status: '実務経験',
    detail: '大学1年から、Web開発と社内業務を支えるシステムの構築を経験。',
    highlights: [
      '毎週水曜日の社内発表会の担当を自動管理・決定するシステムをJavaScriptで構築。社員の繁忙期と前回の発表時期を考慮して担当を決定。',
      '4人チームで温泉のWebサイトを制作。フロントエンド担当の一員として、HTML・CSS・JavaScriptで実装。',
    ],
    tags: ['HTML', 'CSS', 'JavaScript'], url: 'https://www.nowhere.co.jp/ja',
  },
  {
    id: 'abilica', date: '2025.06', duration: '2週間', title: '株式会社アビリカ',
    role: 'インターンシップ / PLC制御', status: '参加済み',
    detail: 'PLC制御とラダープログラミングを、学習から実装・発表まで経験。',
    highlights: [
      '1週目はPLC制御とラダープログラミングの基本的な書き方を学習。',
      '2週目は事業所でラダープログラムを作成し、最終日に自作したシステムを発表。',
    ], tags: ['PLC', 'ラダープログラミング'],
    photos: [
      { src: '/images/abilica-presentation.webp', width: 1500, height: 1000, alt: 'アビリカのインターン最終日に、自作したロボットアームの制御システムを発表している様子', caption: '最終日の成果発表' },
      { src: '/images/abilica-team.webp', width: 1500, height: 1125, alt: 'アビリカのインターン参加者の集合写真', caption: 'インターン参加者との集合写真' },
    ],
  },
  {
    id: 'geniee', date: '2026.03', duration: '2日間', title: '株式会社ジーニー',
    role: 'インターンシップ / Webアプリケーション開発', status: '参加済み',
    detail: 'ドキュメントから仕様を理解し、5人でWebアプリケーションをチーム開発。',
    highlights: ['React・Go・MUIを使い、既存システムの修正と新しいシステムの追加に取り組みました。'],
    tags: ['React', 'Go', 'MUI'],
    photos: [
      { src: '/images/geniee-team.webp', width: 1500, height: 1125, alt: 'ジーニーのインターン参加者がGENIEEのロゴサインの前で撮った集合写真', caption: 'インターン参加者との集合写真' },
    ],
  },
  {
    id: 'rakuten-bank', date: '2026.09.01 — 09.30', title: '楽天銀行',
    role: 'インターンシップ / AI Promotion Department', status: '参加済み',
    detail: 'Difyを使った、組織内の業務改善に取り組みました。',
    highlights: ['組織内の業務改善ワークフローを作成。'],
    tags: ['Dify', 'Workflow'],
  },
  {
    id: 'pksha', date: '2026.10.05 — 10.09', title: 'PKSHA',
    role: '選考インターン / 配属：ITEC', status: '参加予定', upcoming: true,
    detail: 'ITECに配属予定。', highlights: [], tags: [],
  },
  {
    id: 'recruit', date: '2026.10.13 — 11.13', title: 'リクルート',
    role: 'RECRUIT INTERNSHIP for Engineers 2026', status: '参加予定', upcoming: true,
    detail: 'エンジニア向けインターンシップに参加予定。', highlights: [], tags: [],
  },
];
